import config from '../../config';

class QuillFileUploader {
  constructor(quill, options) {
    this.quill = quill;
    this.options = options;
    this.range = null;
    this.loadingPlaceholders = new Map(); // Track loading placeholders
    
    // Save the current selection when the user clicks in the editor
    this.quill.on('selection-change', (range) => {
      if (range) {
        this.range = range;
      }
    });
    
    // Create a file input element
    this.fileInput = document.createElement('input');
    this.fileInput.setAttribute('type', 'file');
    this.fileInput.setAttribute('accept', 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv,.json,.md');
    this.fileInput.style.display = 'none';
    
    // Add the file input to the document
    document.body.appendChild(this.fileInput);
    
    // Handle file selection
    this.fileInput.addEventListener('change', this.handleFileSelection.bind(this));
    
    // Note: Handlers are now registered in ContentEditor.js to avoid conflicts
  }
  
  selectLocalImage() {
    this.fileInput.setAttribute('accept', 'image/*');
    this.fileInput.click();
  }
  
  selectFile() {
    this.fileInput.setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv,.json,.md');
    this.fileInput.click();
  }
  
  async handleFileSelection() {
    if (this.fileInput.files.length === 0) return;
    
    const file = this.fileInput.files[0];
    
    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('File size must be less than 50MB');
      this.fileInput.value = '';
      return;
    }
    
    // Show loading indicator
    const loadingId = `loading-${Date.now()}`;
    const loadingRange = this.insertLoadingPlaceholder(loadingId);
    
    try {
      // Convert file to base64
      const fileContent = await this.fileToBase64(file);
      
      // Upload via API endpoint
      const response = await fetch(`${config.api.url}upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        },
        body: JSON.stringify({
          fileContent: fileContent,
          fileName: file.name,
          fileType: file.type
        })
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      const result = await response.json();
      const fileUrl = result.fileUrl;
      
      // Remove the loading placeholder and insert the file
      this.removeLoadingPlaceholder(loadingId, loadingRange);
      
      // Insert the file into the editor
      if (file.type.startsWith('image/')) {
        this.insertImage(fileUrl, file.name, loadingRange);
      } else {
        this.insertFile(fileUrl, file.name, loadingRange);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      this.removeLoadingPlaceholder(loadingId, loadingRange);
      alert('Failed to upload file. Please try again.');
    }
    
    // Reset the file input
    this.fileInput.value = '';
  }
  
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  }
  
  insertLoadingPlaceholder(id) {
    // Get the current selection or use saved range
    const range = this.range || this.quill.getSelection() || { index: this.quill.getLength(), length: 0 };
    
    // Insert a simple text placeholder that we can track
    const placeholderText = `[Uploading: 0%]`;
    this.quill.insertText(range.index, placeholderText, 'user');
    
    // Store the placeholder info
    this.loadingPlaceholders.set(id, {
      index: range.index,
      length: placeholderText.length,
      originalRange: range
    });
    
    // Move cursor after the placeholder
    this.quill.setSelection(range.index + placeholderText.length);
    
    return { index: range.index, length: placeholderText.length };
  }
  
  updateLoadingPlaceholder(id, percent) {
    const placeholder = this.loadingPlaceholders.get(id);
    if (!placeholder) return;
    
    const newText = `[Uploading: ${percent}%]`;
    
    try {
      // Replace the old placeholder text with updated progress
      this.quill.deleteText(placeholder.index, placeholder.length, 'silent');
      this.quill.insertText(placeholder.index, newText, 'silent');
      
      // Update the stored length
      placeholder.length = newText.length;
    } catch (error) {
      console.warn('Error updating loading placeholder:', error);
    }
  }
  
  removeLoadingPlaceholder(id, fallbackRange = null) {
    const placeholder = this.loadingPlaceholders.get(id);
    if (placeholder) {
      try {
        // Remove the placeholder text
        this.quill.deleteText(placeholder.index, placeholder.length, 'silent');
        this.loadingPlaceholders.delete(id);
        return placeholder;
      } catch (error) {
        console.warn('Error removing loading placeholder:', error);
      }
    }
    
    // Fallback: return the provided range or current selection
    return fallbackRange || this.range || this.quill.getSelection() || { index: this.quill.getLength(), length: 0 };
  }
  
  insertImage(url, altText, targetRange = null) {
    // Use the target range or get current selection
    const range = targetRange || this.range || this.quill.getSelection() || { index: this.quill.getLength(), length: 0 };
    
    try {
      // Insert the image using Quill's embed method
      this.quill.insertEmbed(range.index, 'image', url, 'user');
      
      // Move cursor to the next position
      this.quill.setSelection(range.index + 1);
    } catch (error) {
      console.error('Error inserting image:', error);
      // Fallback: insert as a link
      this.insertFile(url, altText || 'Image', targetRange);
    }
  }
  
  insertFile(url, fileName, targetRange = null) {
    // Use the target range or get current selection
    const range = targetRange || this.range || this.quill.getSelection() || { index: this.quill.getLength(), length: 0 };
    
    try {
      // Create a file link with icon using Quill's link format
      // First insert the icon and filename as text
      const linkText = `📎 ${fileName}`;
      this.quill.insertText(range.index, linkText, 'user');
      
      // Then format it as a link
      this.quill.formatText(range.index, linkText.length, 'link', url, 'user');
      
      // Add custom class for styling (this will be applied via CSS)
      this.quill.formatText(range.index, linkText.length, 'class', 'file-attachment', 'user');
      
      // Move cursor after the link
      this.quill.setSelection(range.index + linkText.length);
    } catch (error) {
      console.error('Error inserting file link:', error);
      
      // Fallback: insert as plain text with URL
      try {
        const fallbackText = `${fileName}: ${url}`;
        this.quill.insertText(range.index, fallbackText, 'user');
        this.quill.setSelection(range.index + fallbackText.length);
      } catch (fallbackError) {
        console.error('Fallback insertion also failed:', fallbackError);
      }
    }
  }
  
  // Cleanup method to remove event listeners and DOM elements
  destroy() {
    if (this.fileInput && this.fileInput.parentNode) {
      this.fileInput.removeEventListener('change', this.handleFileSelection.bind(this));
      this.fileInput.parentNode.removeChild(this.fileInput);
    }
    this.loadingPlaceholders.clear();
  }
}

export default QuillFileUploader;
